/**
 * VerifyEmailPage — the landing page for /verify-email?token=...
 *
 * Reads the single-use token from the URL, POSTs it to
 * /auth/verify-email, and surfaces one of three outcomes:
 *   1. Success — flash a confirmation, then route to /dashboard
 *      (the backend has already returned a JWT and we persisted it).
 *   2. Token problem (expired / already-used / invalid) — show the
 *      reason, offer a "resend verification email" form.
 *   3. Network / server error — show a retry hint.
 *
 * Idempotency: if the user clicks the link twice (or hits refresh on
 * this page), the second redemption returns ALREADY_USED. We treat it
 * gently — "this link was already used; you're verified".
 *
 * TODO (origin-aware verify): the backend currently uses a single
 * APP_BASE_URL env var to build verification links, so verification
 * email always lands on `onbuzz.loxia.ai/verify-email`. When the
 * Autopilot product starts collecting signups too, pass the origin
 * the user registered from in the /auth/register payload, and have
 * the backend use that origin in buildVerificationLink so each
 * product sees its own branded verify page.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { verifyEmail, resendVerification } from '../api/authApi';

type Status = 'verifying' | 'success' | 'token-error' | 'network-error';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string | undefined>();

  // Resend-flow state
  const [resendEmail, setResendEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);

  // Guard against double-redemption in dev when React StrictMode mounts
  // effects twice. Once we have a result we don't retry.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setStatus('token-error');
      setErrorMessage('No verification token in the link.');
      return;
    }

    (async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        // Brief celebratory pause, then send the (now signed-in) user
        // into the app.
        setTimeout(() => navigate('/dashboard', { replace: true }), 1800);
      } catch (err: any) {
        const code: string | undefined = err?.code;
        // Backend codes from /auth/verify-email:
        //   INVALID_TOKEN, EXPIRED_TOKEN, ALREADY_USED, NO_TOKEN
        if (code) {
          setStatus('token-error');
          setErrorCode(code);
          setErrorMessage(err?.message || 'Verification failed.');
        } else {
          setStatus('network-error');
          setErrorMessage(err?.message || 'Could not reach the server.');
        }
      }
    })();
  }, [token, navigate]);

  async function handleResend(e: React.FormEvent) {
    e.preventDefault();
    if (!resendEmail) return;
    setResendBusy(true);
    setResendNotice(null);
    try {
      const r = await resendVerification(resendEmail);
      if (r.retryAfterSec) {
        setResendCooldownSec(r.retryAfterSec);
        setResendNotice(
          `Please wait ${r.retryAfterSec}s before requesting another email.`,
        );
      } else {
        // Backend deliberately returns the same generic message whether
        // the account exists or not — we mirror that wording.
        setResendNotice(
          'If an account with that email is pending verification, a new link has been sent.',
        );
      }
    } catch (_err) {
      setResendNotice('Something went wrong. Please try again in a minute.');
    } finally {
      setResendBusy(false);
    }
  }

  // ─── Rendering ──────────────────────────────────────────────────
  if (status === 'verifying') {
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 440, width: '100%', textAlign: 'center' }}>
        <CircularProgress size={32} sx={{ mb: 2 }} />
        <Typography variant="h6">Verifying your email…</Typography>
      </Paper>
    );
  }

  if (status === 'success') {
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          You're in 🎉
        </Typography>
        <Alert severity="success" sx={{ mb: 2 }}>
          Your email is verified. Taking you to your dashboard…
        </Alert>
      </Paper>
    );
  }

  // token-error OR network-error
  const isAlreadyUsed = errorCode === 'ALREADY_USED';
  const isExpired     = errorCode === 'EXPIRED_TOKEN';

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 480, width: '100%' }}>
      <Typography variant="h5" component="h1" gutterBottom align="center">
        {isAlreadyUsed ? 'Already verified' : 'Verification failed'}
      </Typography>

      <Alert severity={isAlreadyUsed ? 'info' : 'warning'} sx={{ mb: 3 }}>
        {isAlreadyUsed
          ? 'This link was already used. You can sign in now.'
          : errorMessage || 'We couldn\'t verify your email with this link.'}
      </Alert>

      {isAlreadyUsed ? (
        <Box textAlign="center">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <Button variant="contained" color="primary">Go to sign-in</Button>
          </Link>
        </Box>
      ) : (
        <>
          {/* Expired / invalid → offer resend */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isExpired
              ? 'This link has expired (links are valid for 24 hours). Enter your email below and we\'ll send a fresh one.'
              : 'Enter the email you registered with and we\'ll send a new verification link.'}
          </Typography>

          <Box component="form" onSubmit={handleResend} noValidate>
            <TextField
              type="email"
              label="Email"
              fullWidth
              required
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              disabled={resendBusy}
              margin="normal"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={resendBusy || !resendEmail || resendCooldownSec > 0}
              sx={{ mt: 1 }}
            >
              {resendBusy ? <CircularProgress size={22} /> : 'Resend verification email'}
            </Button>
            {resendNotice && (
              <Alert severity="info" sx={{ mt: 2 }}>{resendNotice}</Alert>
            )}
            <Box textAlign="center" mt={2}>
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">Back to sign-in</Typography>
              </Link>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default VerifyEmailPage;
